import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Contextos
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

// API
import { useParticipanteService } from '../../services/participanteServices';

// Componentes
import PhoneInput from '../../components/PhoneInput/PhoneInput';

// Utils
import { createEmptyPhoneModel, toPhonePayload } from '../../utils/phoneCountries';

// Estilos
import styles from './participantecadastropage.module.css';

/**
 * Le state.from e tenta inferir:
 * - turmaHash
 * - dest ('res' | 'ead')
 * Aceita /res/:hash, /responder/:hash e /ead/:hash
 */
function parseFrom(location) {
  const from = location?.state?.from || null;
  const pathname = from?.pathname || '';
  const search = from?.search || '';
  const hash = from?.hash || '';

  const match = pathname.match(/^\/(res(?:ponder)?|ead)\/([^/?#]+)/i);
  const rawDest = match?.[1] || null;
  const turmaHash = match?.[2] || null;

  const dest = rawDest
    ? (rawDest.toLowerCase().startsWith('ead') ? 'ead' : 'res')
    : '';

  return { from, pathname, search, hash, turmaHash, dest };
}

/** Hook para ler ?turma=...&dest=... da query string */
function useQueryParams(location) {
  return useMemo(() => {
    try {
      const usp = new URLSearchParams(location.search || '');
      const turma = (usp.get('turma') || '').trim();
      const dest = (usp.get('dest') || '').trim().toLowerCase();
      return {
        turma: turma || '',
        dest: (dest === 'res' || dest === 'ead') ? dest : '',
      };
    } catch {
      return { turma: '', dest: '' };
    }
  }, [location.search]);
}

/**
 * Decide para onde voltar apos cadastro+login:
 * 1) se tiver state.from.pathname -> volta pra la
 * 2) senao, se tiver turma + dest (res|ead) -> volta /dest/turma
 * 3) senao -> /participante/page
 */
function getRedirectPath(location, turma, dest) {
  const fromPath = location?.state?.from?.pathname || '';
  if (fromPath) return fromPath;

  if (turma && (dest === 'res' || dest === 'ead')) {
    return `/${dest}/${encodeURIComponent(turma)}`;
  }

  return '/participante/page';
}

function ParticipanteCadastroPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { loginParticipante } = useAuth();
  const { criarParticipante } = useParticipanteService();

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [celular, setCelular] = useState(() => createEmptyPhoneModel());
  const [senha, setSenha] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [celularErro, setCelularErro] = useState('');

  // Query: ?turma=...&dest=...
  const qp = useQueryParams(location);

  // State.from: /res/:hash ou /ead/:hash
  const fromInfo = useMemo(() => parseFrom(location), [location]);

  // Efetivos (prioriza query; depois state.from)
  const turmaHashEfetivo = useMemo(
    () => qp.turma || fromInfo.turmaHash || '',
    [qp.turma, fromInfo.turmaHash]
  );

  const destEfetivo = useMemo(
    () => qp.dest || fromInfo.dest || '',
    [qp.dest, fromInfo.dest]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCarregando(true);
    setErro('');
    setCelularErro('');

    if (!celular.isValid) {
      showToast(t('phoneCountry.invalid'), 3000, 'error');
      setCelularErro(t('phoneCountry.invalid'));
      setCarregando(false);
      return;
    }

    // No fluxo de convite via /res ou /ead, turma deve existir para criar ja vinculado
    if (!turmaHashEfetivo) {
      showToast(
        t('auth.classNotIdentified'),
        5000,
        'error'
      );
      setCarregando(false);
      return;
    }

    const dados = {
      nome,
      email: email || '',
      ...toPhonePayload('celular', celular),
      senha,
      loginPor: 'celular',
      idTurma: turmaHashEfetivo, // hash criptografado da turma
    };

    try {
      const response = await criarParticipante(dados);

      if (response?.success) {
        showToast(t('auth.registrationSuccess'), 3000, 'success');

        // Login automatico
        const loginResult = await loginParticipante({
          celular: response.celular || response.data?.celular || celular.canonicalValue,
          pais: celular.countryIso,
          senha,
        });
        if (loginResult?.success) {
          const destino = getRedirectPath(location, turmaHashEfetivo, destEfetivo || 'res');
          navigate(destino, { replace: true });
        } else {
          showToast(t('auth.automaticLoginError'), 3000, 'error');
          setErro(t('auth.automaticLoginError'));
        }
      } else {
        const phoneError = response?.code === 'invalid_celular'
          ? t('phoneCountry.invalid')
          : response?.code === 'duplicate_celular'
            ? t('phoneCountry.duplicate')
            : '';
        if (phoneError) setCelularErro(phoneError);
        showToast(phoneError || response?.message || t('auth.registrationError'), 4000, 'error');
        setErro(phoneError || response?.message || t('auth.registrationError'));
      }
    } catch {
      showToast(t('auth.serverConnectionError'), 4000, 'error');
      setErro(t('auth.serverConnectionError'));
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('auth.registrationTitle')}</h2>

      <form onSubmit={handleSubmit} className={styles.form}>
        <input
          type="text"
          placeholder={t('auth.fullName')}
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className={styles.input}
          required
        />

        <input
          type="email"
          placeholder={t('auth.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={styles.input}
          required
        />

        <PhoneInput
          value={celular}
          onChange={setCelular}
          label={t('phoneCountry.cellphoneLabel')}
          error={celularErro}
          disabled={carregando}
          required
        />

        <input
          type="password"
          placeholder={t('auth.password')}
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          className={styles.input}
          required
        />

        <button type="submit" className={styles.button} disabled={carregando}>
          {carregando ? t('auth.registering') : t('auth.register')}
        </button>

        {erro && <p className={styles.error}>{erro}</p>}

        <p>{t('auth.registrationHelp')}</p>
      </form>
    </div>
  );
}

export default ParticipanteCadastroPage;
